import { SIDE_PANEL_WIDTH } from '@/constants/side-panel'
import '../../locales/register'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { InView } from 'react-intersection-observer'
import { Bell, X } from 'lucide-react'
import {
    AppPanel,
    AppPanelBody,
    AppPanelHeader,
} from '@vnetwork/datacentral-ui/layout'
import {
    IconButton,
    SegmentedControl,
    Spinner,
    Text,
} from '@vnetwork/datacentral-ui/primitives'
import { EmptyState } from '@vnetwork/datacentral-ui/data'
import { useNotifications } from '../../hooks/useNotifications'
import NotificationItemComp from './PopupNotification/NotificationItem'

/** Notifications docked beside the page (DashboardLayout `panel`). */
const NotificationPanel: React.FC<{ onClose: () => void }> = ({ onClose }) => {
    const { t } = useTranslation()
    const [limitCount, setLimitCount] = useState(10)
    const { notifications, loading, error } = useNotifications(limitCount)
    const hasNextPage = notifications.length >= limitCount
    const [filter, setFilter] = useState<'all' | 'unread'>('all')
    const visible =
        filter === 'unread'
            ? notifications.filter((n) => !n.read)
            : notifications

    return (
        <AppPanel width={SIDE_PANEL_WIDTH} aria-label={t('notification')}>
            <AppPanelHeader
                className="!h-[var(--vn-chrome-row-height)]"
                actions={
                    <IconButton
                        aria-label={t('close')}
                        size="sm"
                        onClick={onClose}
                    >
                        <X size={16} />
                    </IconButton>
                }
            >
                <Bell size={18} className="shrink-0 text-primary" />
                <Text weight="semibold" truncate>
                    {t('notification')}
                </Text>
            </AppPanelHeader>
            <div className="border-b border-border px-4 py-2">
                <SegmentedControl
                    size="sm"
                    aria-label={t('notification')}
                    value={filter}
                    onValueChange={(v) => setFilter(v as 'all' | 'unread')}
                    items={[
                        { value: 'all', label: t('all') },
                        {
                            value: 'unread',
                            label: t('notification:unread'),
                        },
                    ]}
                />
            </div>
            <AppPanelBody className="p-0">
                {visible.map((n) => (
                    <NotificationItemComp key={n.id} item={n} />
                ))}

                {error && (
                    <Text as="div" className="p-4" tone="danger">
                        {t('something-wrong')}
                    </Text>
                )}

                {loading && !notifications.length && (
                    <div className="flex justify-center p-4">
                        <Spinner />
                    </div>
                )}

                {!error && !loading && visible.length === 0 && (
                    <EmptyState size="sm" title={t('notification-empty')} />
                )}

                <InView
                    fallbackInView
                    onChange={(inView) => {
                        if (!loading && inView && hasNextPage) {
                            setLimitCount((prev) => prev + 10)
                        }
                    }}
                >
                    {({ ref }) => (
                        <div
                            ref={ref}
                            className="flex w-full items-center justify-center py-4"
                        >
                            {loading && notifications.length > 0 && <Spinner />}
                        </div>
                    )}
                </InView>
            </AppPanelBody>
        </AppPanel>
    )
}

export default NotificationPanel
